import { useCallback, useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { Button, InputGroup } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import { useSendEmailVerificationCode, useVerifyEmailWithCode } from '@/features/profile/hooks/user'
import { useLogout } from '@/features/auth/hooks/auth'
import toast from 'react-hot-toast'
import AuthLayout from '../../components/AuthLayout'

const RESEND_COOLDOWN_SECONDS = 60

export default function EmailVerificationPage() {
  const { t } = useI18n()
  const { session } = useSession()
  const [code, setCode] = useState('')
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0)

  const { mutateAsync: sendCode, isPending: isSendingCode } = useSendEmailVerificationCode()
  const { mutateAsync: verifyCode, isPending: isVerifying } = useVerifyEmailWithCode()
  const { mutateAsync: logout } = useLogout()

  useEffect(() => {
    if (resendCooldownSeconds <= 0) {
      return
    }
    const timer = setInterval(() => {
      setResendCooldownSeconds(prev => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [resendCooldownSeconds])

  const handleResendCode = useCallback(async () => {
    try {
      await sendCode()
      setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS)
      toast.success(t('common:emailVerificationPage.notifications.codeResendSuccess'))
    } catch {
      toast.error(t('common:emailVerificationPage.notifications.genericError'))
    }
  }, [sendCode, t])

  const handleVerify = useCallback(
    async (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault()
      if (!code.trim()) {
        return
      }

      try {
        await verifyCode(code.trim())
        toast.success(t('common:emailVerificationPage.notifications.codeVerificationSuccess'))
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined
        if (message === 'INVALID_VERIFICATION_CODE') {
          toast.error(t('common:emailVerificationPage.notifications.invalidCodeError'))
        } else if (message === 'VERIFICATION_CODE_EXPIRED') {
          toast.error(t('common:emailVerificationPage.notifications.expiredCodeError'))
        } else {
          toast.error(t('common:emailVerificationPage.notifications.genericError'))
        }
      }
    },
    [code, verifyCode, t]
  )

  return (
    <AuthLayout showCarousel={false}>
      <div className="flex flex-col items-center space-y-4 p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">{t('common:emailVerificationPage.title')}</h2>
          <p className="text-text-secondary text-sm">
            <Trans
              i18nKey="common:emailVerificationPage.welcomeMessage"
              values={{ email: session?.email ?? '' }}
              components={{ bold: <strong />, br: <br /> }}
            />
          </p>
        </div>
        <form onSubmit={handleVerify} className="space-y-4 w-full mb-8">
          <InputGroup
            label={t('common:emailVerificationPage.codeInputLabel')}
            value={code}
            onChange={evt => setCode(evt.target.value)}
            name="verificationCode"
            placeholder="000000"
            disabled={isVerifying}
          />
          <Button
            type="submit"
            className="w-full"
            isLoading={isVerifying}
            disabled={isVerifying || !code.trim()}
          >
            {t('common:emailVerificationPage.actions.verifyCode')}
          </Button>
          <div className="text-center mt-2 space-y-1">
            <Button
              type="button"
              variant="subtle"
              sizeType="xs"
              onClick={handleResendCode}
              isLoading={isSendingCode}
              disabled={resendCooldownSeconds > 0 || isSendingCode}
            >
              {resendCooldownSeconds > 0
                ? t('common:emailVerificationPage.resendIn', {
                    seconds: resendCooldownSeconds
                  })
                : t('common:emailVerificationPage.actions.resendCode')}
            </Button>
          </div>
        </form>
        <div className="text-sm px-10 text-center">
          <button type="button" onClick={() => logout()} className="text-green-600 underline">
            {t('common:navbar.userMenu.logout')}
          </button>
        </div>
      </div>
    </AuthLayout>
  )
}
